import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility,
  Search,
  Email,
  Business,
  Person,
  Badge,
} from '@mui/icons-material';
import AddUserModal from '../../components/users/AddUserModal';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { userService, companyService } from '../../services/api';

const ROLE_LABELS = {
  superAdmin: 'Super Admin',
  coach: 'Coach',
  companyAdmin: 'Company Admin',
  manager: 'Manager',
  employee: 'Employee',
};

const ROLE_COLORS = {
  superAdmin: 'error',
  coach: 'secondary',
  companyAdmin: 'primary',
  manager: 'warning',
  employee: 'default',
};

const NAVBAR_HEIGHT = 64;
const ROLE_HIERARCHY = { companyAdmin: 3, manager: 2, employee: 1 };

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editReportsToOptions, setEditReportsToOptions] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companies, setCompanies] = useState([]);

  const { user: currentUser } = useSelector((state) => state.auth);

  const canManage = ['superAdmin', 'coach', 'companyAdmin', 'manager'].includes(currentUser?.role);
  const isMultiCompany = ['superAdmin', 'coach'].includes(currentUser?.role);

  const fetchUsers = async (cid) => {
    try {
      setLoading(true);
      const response = await userService.getUsers(cid);
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(companyFilter);
  }, [companyFilter]);

  useEffect(() => {
    if (isMultiCompany) {
      companyService.getCompanies()
        .then(res => setCompanies(res.data || []))
        .catch(() => {});
    }
  }, [isMultiCompany]);

  // Fetch "Reporting to" options when edit dialog opens
  useEffect(() => {
    if (!openEditModal || !currentUserData) { setEditReportsToOptions([]); return; }
    const companyId = currentUserData.company?._id || currentUserData.company;
    const role = currentUserData.role;
    if (!companyId || !role) { setEditReportsToOptions([]); return; }
    const currentLevel = ROLE_HIERARCHY[role] || 0;
    if (currentLevel === 0) { setEditReportsToOptions([]); return; }
    userService.getUsers(companyId)
      .then(res => setEditReportsToOptions(
        res.data.filter(u => (ROLE_HIERARCHY[u.role] || 0) > currentLevel && u._id !== currentUserData._id)
      ))
      .catch(() => {});
  }, [openEditModal, currentUserData?.role, currentUserData?.company]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q) ||
      u.company?.name?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const paginatedUsers = useMemo(() =>
    filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };


  const handleAddUser = async (userData) => {
    try {
      setLoading(true);
      await userService.createUser(userData);
      await fetchUsers(companyFilter);
      setOpenAddModal(false);
      setSuccessMessage('User created successfully');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (userData) => {
    try {
      setLoading(true);
      await userService.updateUser(currentUserData._id, userData);
      setSuccessMessage('User updated successfully');
      setOpenEditModal(false);
      fetchUsers(companyFilter);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      await userService.deleteUser(userToDelete._id);
      setSuccessMessage('User deleted successfully');
      setOpenDeleteDialog(false);
      fetchUsers(companyFilter);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Users</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMultiCompany && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={companyFilter}
                onChange={e => { setCompanyFilter(e.target.value); setPage(0); setSearch(''); }}
                displayEmpty
                sx={{ fontSize: 14 }}
              >
                <MenuItem value="all">All Companies</MenuItem>
                {companies.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {canManage && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenAddModal(true)}>
              Add User
            </Button>
          )}
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by name, email, role or company…"
          value={search}
          onChange={handleSearchChange}
          size="small"
          sx={{ width: 340 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Company</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    {users.length === 0 ? 'No users found.' : 'No users match your search.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={ROLE_LABELS[user.role] || user.role}
                      color={ROLE_COLORS[user.role] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.company?.name || '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => setViewUser(user)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManage && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setCurrentUserData(user); setOpenEditModal(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManage && (
                      <Tooltip title="Delete">
                        <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => { setUserToDelete(user); setOpenDeleteDialog(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:"
        />
      </TableContainer>

      {/* View Details Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(viewUser)}
        onClose={() => setViewUser(null)}
        sx={{ '& .MuiDrawer-paper': { top: NAVBAR_HEIGHT, height: `calc(100% - ${NAVBAR_HEIGHT}px)`, width: 340, p: 3 } }}
      >
        {viewUser && (
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>{viewUser.name}</Typography>
            <Chip
              label={ROLE_LABELS[viewUser.role] || viewUser.role}
              color={ROLE_COLORS[viewUser.role] || 'default'}
              size="small"
              sx={{ mb: 2 }}
            />
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">{viewUser.email}</Typography>
              </Box>
              {viewUser.company && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">{viewUser.company.name}</Typography>
                </Box>
              )}
              {viewUser.department && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Badge sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">{viewUser.department}</Typography>
                </Box>
              )}
              {viewUser.manager && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">Manager: {viewUser.manager?.name || viewUser.manager}</Typography>
                </Box>
              )}
              <Divider />
              <Box>
                <Typography variant="caption" color="textSecondary">Created</Typography>
                <Typography variant="body2">
                  {viewUser.createdAt
                    ? new Date(viewUser.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                    : '—'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Add User Modal */}
      <AddUserModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSubmit={handleAddUser}
      />

      {/* Edit User Dialog */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              defaultValue={currentUserData?.name || ''}
              onChange={(e) => setCurrentUserData({ ...currentUserData, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              defaultValue={currentUserData?.email || ''}
              onChange={(e) => setCurrentUserData({ ...currentUserData, email: e.target.value })}
              type="email"
            />
            {currentUser?.role !== 'manager' && (
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={currentUserData?.role || ''}
                  label="Role"
                  onChange={(e) => setCurrentUserData({ ...currentUserData, role: e.target.value, reportsTo: '' })}
                >
                  {['employee', 'manager', 'companyAdmin'].map((role) => (
                    <MenuItem key={role} value={role}>
                      {ROLE_LABELS[role] || role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {editReportsToOptions.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Reporting to</InputLabel>
                <Select
                  value={currentUserData?.reportsTo?._id || currentUserData?.reportsTo || ''}
                  label="Reporting to"
                  onChange={(e) => setCurrentUserData({ ...currentUserData, reportsTo: e.target.value })}
                >
                  <MenuItem value="">— None —</MenuItem>
                  {editReportsToOptions.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.name} ({ROLE_LABELS[u.role] || u.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
          <Button onClick={() => handleEditUser(currentUserData)} variant="contained" color="primary" disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage('')}>
        <Alert onClose={() => setSuccessMessage('')} severity="success">{successMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default Users;
